import React from 'react';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICycleRequest, ICycleSummary } from '../../interfaces/appraisal.interface';

export const emptyCycleRequest: ICycleRequest = {
    name: '', periodStart: '', periodEnd: '', selfReviewDue: '', managerReviewDue: '',
};

export const cycleRequestFrom = (c: ICycleSummary): ICycleRequest => ({
    name: c.name,
    periodStart: c.periodStart,
    periodEnd: c.periodEnd,
    selfReviewDue: c.selfReviewDue,
    managerReviewDue: c.managerReviewDue,
});

export type CycleFormError = 'nameRequired' | 'datesRequired' | 'periodOrder' | 'dueOrder';

/** Mirrors the server's checks so the admin sees the problem before saving. */
export const validateCycleRequest = (r: ICycleRequest): CycleFormError | null => {
    if (!r.name.trim()) return 'nameRequired';
    if (!r.periodStart || !r.periodEnd || !r.selfReviewDue || !r.managerReviewDue) return 'datesRequired';
    if (r.periodStart > r.periodEnd) return 'periodOrder';
    if (r.selfReviewDue > r.managerReviewDue) return 'dueOrder';
    return null;
};

interface Props {
    value: ICycleRequest;
    onChange: (value: ICycleRequest) => void;
    disabled?: boolean;
}

const CycleDetailsForm: React.FC<Props> = ({ value, onChange, disabled }) => {
    const { t } = useTranslation('appraisal');
    const set = (field: keyof ICycleRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
        onChange({ ...value, [field]: e.target.value });
    const date = (field: keyof ICycleRequest, label: string) => (
        <TextField
            type="date" label={label} value={value[field]} onChange={set(field)} disabled={disabled}
            InputLabelProps={{ shrink: true }} required fullWidth
        />
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
                label={t('cycleForm.name')} placeholder={t('cycleForm.namePlaceholder')} value={value.name}
                onChange={set('name')} disabled={disabled} required fullWidth inputProps={{ maxLength: 120 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                {date('periodStart', t('cycleForm.periodStart'))}
                {date('periodEnd', t('cycleForm.periodEnd'))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                {date('selfReviewDue', t('cycleForm.selfReviewDue'))}
                {date('managerReviewDue', t('cycleForm.managerReviewDue'))}
            </Box>
        </Box>
    );
};

export default CycleDetailsForm;
